import React, { ReactNode, useEffect, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import style from "./index.module.scss";
import { DropdownTriangle } from "./DropdownTriangle";

export type NotificationIntent = "info" | "warning" | "danger";

export interface INotificationItem {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  timestamp?: ReactNode;
  intent?: NotificationIntent;
}

interface NotificationsMenuProps {
  items?: INotificationItem[];
}

const STORAGE_KEY = "notifications:seenIds";

export const NotificationsMenu: React.FC<NotificationsMenuProps> = ({ items = [] }) => {
  const [open, setOpen] = useState(false);
  const [seenIds, setSeenIds] = useState<string[]>(() => {
    if (typeof window === "undefined") {
      return [];
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((value) => typeof value === "string");
      }
      return [];
    } catch {
      return [];
    }
  });

  const hasUnread = items.some((item) => !seenIds.includes(item.id));

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (items.length && hasUnread) {
      setOpen(true);
    }
  }, [items, hasUnread]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && typeof window !== "undefined") {
      const nextSeen = Array.from(new Set([...seenIds, ...items.map((item) => item.id)]));
      setSeenIds(nextSeen);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSeen));
    }
  };

  const hasItems = items.length > 0;

  return (
    <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={style.notificationsToggle}
          data-state={open ? "open" : "closed"}
          aria-label="Notifications"
        >
          <i
            className={["far", "fa-bell", style.notificationsIcon].join(" ")}
            aria-hidden="true"
          />
          {hasUnread && <span className={style.notificationsDot} />}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={[style.userMenuPanel, style.notificationsPanel].join(" ")}
          align="end"
          sideOffset={16}
          alignOffset={-12}
        >
          <DropdownTriangle className={style.menuTriangle} />
          {hasItems ? (
            <div className={style.notificationsList}>
              {items.map((item) => (
                <div
                  key={item.id}
                  className={[
                    style.notificationsItem,
                    item.intent === "danger"
                      ? style.notificationsItemDanger
                      : item.intent === "warning"
                        ? style.notificationsItemWarning
                        : style.notificationsItemInfo,
                    item.description ? style.notificationsItemWithDescription : "",
                  ].join(" ")}
                >
                  <div className={style.notificationsItemTitle}>{item.title}</div>
                  {item.description && (
                    <div className={style.notificationsItemDescription}>
                      {item.description}
                    </div>
                  )}
                  {item.timestamp && (
                    <div className={style.notificationsItemTimestamp}>
                      {item.timestamp}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={style.notificationsEmpty}>No notifications yet</div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
